import classNames from 'classnames';
import { ButtonBrand } from 'components/ButtonBrand';
import { sanitizeHtml } from 'helpers/utils';
import { SECTION_LAYOUT } from 'store/sets/types';
import type { ISection } from 'store/sets/types';

const CENTERED_LAYOUTS: readonly string[] = [
  SECTION_LAYOUT.NO_IMAGE,
  SECTION_LAYOUT.IMAGE_CENTER_TOP,
  SECTION_LAYOUT.IMAGE_CENTER_BOTTOM,
];

const isCenteredLayout = (layout?: string) => layout != null && CENTERED_LAYOUTS.includes(layout);

const isImageLeft = (layout?: string) => layout === SECTION_LAYOUT.IMAGE_LEFT;
const isImageRight = (layout?: string) => layout === SECTION_LAYOUT.IMAGE_RIGHT;

export type SetSectionProps = {
  section: ISection;
  tertiaryColor: string;
  imageUrl?: string;
};

export const SetSection = ({ section, tertiaryColor, imageUrl }: SetSectionProps) => {
  const {
    background_color,
    text_color,
    button_copy,
    cta_button_color,
    has_image,
    description,
    layout,
    redirect_link,
    show_button,
    title,
  } = section;

  const centered = isCenteredLayout(layout);
  const imageLeft = isImageLeft(layout);
  const imageRight = isImageRight(layout);
  const showImage = has_image;
  const imageTop = layout === SECTION_LAYOUT.IMAGE_CENTER_TOP;
  const imageBottom = layout === SECTION_LAYOUT.IMAGE_CENTER_BOTTOM;

  const sectionClass = classNames('set-section', {
    'set-section--centered': centered,
    'set-section--side-image': imageLeft || imageRight,
    'set-section--image-left': imageLeft,
    'set-section--image-right': imageRight,
    'set-section--image-top': showImage && imageTop,
    'set-section--image-bottom': showImage && imageBottom,
  });

  const contentBlock = (
    <div className="set-section__content">
      {title && (
        <span
          className="set-section__title h2-dtm"
          style={text_color ? { color: text_color } : undefined}
        >
          {title}
        </span>
      )}
      {description && (
        <span
          className="set-section__description description"
          style={text_color ? { color: text_color } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
        />
      )}
      {show_button && button_copy && redirect_link && (
        <div className="set-section__cta">
          <ButtonBrand
            $backgroundColor={cta_button_color}
            $width="360px"
            href={redirect_link}
            target="_blank"
            $gradientColor={tertiaryColor}
          >
            {button_copy}
          </ButtonBrand>
        </div>
      )}
    </div>
  );

  const imageBlock =
    showImage && imageUrl ? (
      <div className="set-section__image-wrapper">
        <img alt="" className="set-section__image" src={imageUrl} />
      </div>
    ) : null;

  return (
    <section
      className={sectionClass}
      style={background_color ? { backgroundColor: background_color } : undefined}
    >
      <div className={classNames('set-section__inner', { centered: isCenteredLayout })}>
        {imageBlock}
        {contentBlock}
      </div>
    </section>
  );
};
